import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import useQuizStore from '../../store/quizStore';
import useNavStore from '../../store/navStore';
import useEnsureScopeNav from '../../hooks/useEnsureScopeNav';
import ThemeToggle from '../ui/ThemeToggle';
import BrandLogo, { BrandMark, FaviconMark } from '../ui/BrandLogo';
import { User, LogOut, ChevronLeft, ChevronRight, Plus, Search, X, LayoutGrid, Eye, Lock, Check } from 'lucide-react';
import api from '../../services/api';
import Illustration from '../ui/Illustration';
import illuAvancement from '../../assets/illustrations/menu/mon-avancement.svg?raw';
import illuConfig from '../../assets/illustrations/menu/configuration-thematiques.svg?raw';
import illuInfos from '../../assets/illustrations/menu/mes-infos.svg?raw';
import illuAchats from '../../assets/illustrations/menu/mes-achats.svg?raw';
import illuParrainer from '../../assets/illustrations/menu/parrainer.svg?raw';
import illuCode from '../../assets/illustrations/menu/activer-un-code.svg?raw';
import illuFormateur from '../../assets/illustrations/menu/espace-formateur.svg?raw';
import illuAdmin from '../../assets/illustrations/menu/administration.svg?raw';
import illuGuide from '../../assets/illustrations/menu/guide.svg?raw';
import illuFaq from '../../assets/illustrations/menu/faq.svg?raw';
import illuCgu from '../../assets/illustrations/menu/cgu.svg?raw';
import illuThematiques from '../../assets/illustrations/menu/thematiques.svg?raw';
import illuFonctionnalites from '../../assets/illustrations/menu/fonctionnalites.svg?raw';
import illuQuestions from '../../assets/illustrations/menu/questions-frequentes.svg?raw';
import illuMaThematique from '../../assets/illustrations/menu/ma-thematique.svg?raw';
import illuCreer from '../../assets/illustrations/menu/creer-un-compte.svg?raw';
import illuConnexion from '../../assets/illustrations/menu/se-connecter.svg?raw';

const SCOPE_LABELS = {
    'fonction-publique-territoriale': 'Fonction publique territoriale',
    'fonction-publique-etat': 'Fonction publique d\'État',
    'police-gendarmerie': 'Police & Gendarmerie',
    'crpe': 'CRPE',
    'tests-psychotechniques': 'Tests psychotechniques',
    'sante-social': 'Secteur sanitaire & social',
};

const GESTION_LABELS = {
    'questions': 'Questions',
    'content': 'Contenu',
    'users': 'Utilisateurs',
    'orders': 'Commandes',
    'seo': 'SEO',
};

function slugToLabel(slug) {
    return SCOPE_LABELS[slug] || slug.replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase());
}

export function getLogoTarget(user) {
    if (!user) return '/';
    try {
        const prefs = JSON.parse(localStorage.getItem('monquiz_dashboard_prefs') || '{}');
        if (prefs.lastScope) return `/compte/quiz/${prefs.lastScope}`;
    } catch { /* ignore */ }
    return '/compte';
}

export default function Header() {
    const { user, logout, impersonating, stopImpersonation, startImpersonation, adminGate } = useAuthStore();
    // Portail admin actif mais pas encore franchi sur cet appareil → bloquer la recherche
    // d'impersonation et demander le mot de passe au lieu d'un 403 silencieux.
    const gateBlocked = !!(adminGate?.active && !adminGate?.passed);
    const { quizInfo, currentIndex, questions, answers, finished, isReview } = useQuizStore();
    const { scopeNav } = useNavStore();
    // Dessins des menus aux couleurs de la thématique affichée ; sans thématique (accueil, pages publiques),
    // violet foncé de la marque (--primary-dark), plutôt que le bordeaux par défaut des illustrations.
    const menuColor = scopeNav?.scopes?.find(s => s.slug === scopeNav.currentScope)?.couleur_theme || '#6d28d9';
    // Repeuple le menu de thématiques s'il a été perdu (reload sur une page hors-scope).
    useEnsureScopeNav();
    const navigate = useNavigate();
    const location = useLocation();
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);
    const isHomePage = location.pathname === '/';

    // Impersonation search
    const [impSearch, setImpSearch] = useState('');
    const [impResults, setImpResults] = useState([]);
    const [impLoading, setImpLoading] = useState(false);
    const impTimerRef = useRef(null);

    const searchUsers = useCallback((query) => {
        if (impTimerRef.current) clearTimeout(impTimerRef.current);
        if (query.length < 2) { setImpResults([]); return; }
        impTimerRef.current = setTimeout(async () => {
            setImpLoading(true);
            try {
                const { data } = await api.get('/admin/users.php', { params: { search: query, per_page: 10 } });
                setImpResults(data.data?.users || []);
            } catch { setImpResults([]); }
            setImpLoading(false);
        }, 300);
    }, []);

    const handleImpersonate = async (u) => {
        setImpSearch('');
        setImpResults([]);
        setMenuOpen(false);
        await startImpersonation({ id: u.id, email: u.email, prenom: u.prenom });
        // Clear local dashboard prefs to avoid redirecting based on admin's prefs
        localStorage.removeItem('monquiz_dashboard_prefs');
        // Full reload (pas une nav SPA) pour que tout l'affichage se rafraîchisse
        // avec les données de l'utilisateur simulé (scopes, historique, abos…).
        window.location.assign('/compte/quiz/mes-thematiques');
    };

    // Scope nav scroll (header desktop)
    const headerNavRef = useRef(null);
    const [headerNavScroll, setHeaderNavScroll] = useState({ left: false, right: false });
    const checkHeaderNavScroll = useCallback(() => {
        const el = headerNavRef.current;
        if (!el) return;
        setHeaderNavScroll({
            left: el.scrollLeft > 2,
            right: el.scrollLeft < el.scrollWidth - el.clientWidth - 2,
        });
    }, []);
    useEffect(() => {
        if (!scopeNav) return;
        const t1 = setTimeout(() => {
            checkHeaderNavScroll();
            const el = headerNavRef.current;
            if (!el) return;
            const active = el.querySelector('.header-scope-pill.active');
            if (active) active.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'center' });
            checkHeaderNavScroll();
        }, 100);
        window.addEventListener('resize', checkHeaderNavScroll);
        return () => { clearTimeout(t1); window.removeEventListener('resize', checkHeaderNavScroll); };
    }, [scopeNav, checkHeaderNavScroll]);

    const pathParts = location.pathname.split('/').filter(Boolean);
    const isQuizActive = user && quizInfo && !finished && !isReview && pathParts.includes('quiz');
    const isLandingPreview = location.pathname.startsWith('/landing');

    const isQuizCodePage = location.pathname === '/quiz-code';
    const isCategoryCodePage = location.pathname === '/category-code';

    // Le menu de thématiques (scope-nav) persiste dans l'espace connecté.
    // On le masque uniquement sur la config des thématiques (page dédiée à ce réglage)
    // et sur l'admin (qui a son propre fil d'ariane).
    const isThematiqueConfigPage = pathParts[0] === 'compte' && pathParts[1] === 'account' && pathParts[2] === 'thematiques';
    const isAdminPage = pathParts[0] === 'gestion';

    // Nav marketing persistante : accueil (tout le monde) + toutes les pages publiques
    // pour les visiteurs non connectés. Exclue les pages d'accès par code (contexte
    // fonctionnel) ; pour les connectés hors accueil, c'est la nav de l'app qui s'affiche.
    const showQuickNav = isHomePage || isLandingPreview || (!user && !isQuizCodePage && !isCategoryCodePage);

    function renderHeaderCenter() {
        if (isQuizCodePage) {
            return (
                <div className="header-center header-center--quiz-code" style={{ position: 'absolute', left: 0, right: 0, pointerEvents: 'none' }}>
                    <span className="header-center-label" style={{ pointerEvents: 'auto' }}>Acc&egrave;s &agrave; un Quiz par code</span>
                </div>
            );
        }
        if (isCategoryCodePage) {
            return (
                <div className="header-center header-center--quiz-code" style={{ position: 'absolute', left: 0, right: 0, pointerEvents: 'none' }}>
                    <span className="header-center-label" style={{ pointerEvents: 'auto' }}>Acc&egrave;s &agrave; un panel de quiz par code</span>
                </div>
            );
        }
        if (isHomePage || isLandingPreview) return null;
        if (!user) return null;

        if (isQuizActive) {
            const total = questions.length;
            const answered = Object.keys(answers).length;
            return (
                <div className="header-center">
                    <span className="header-center-label">{quizInfo.label}</span>
                    <span className="header-center-sep" aria-hidden>·</span>
                    <span className="header-center-progress">
                        <span className="header-center-progress-bar">
                            <span className="header-center-progress-fill" style={{ width: `${total ? (answered / total) * 100 : 0}%` }} />
                        </span>
                        <span className="header-center-progress-text">{currentIndex + 1}<span className="header-center-progress-total">/{total}</span></span>
                    </span>
                </div>
            );
        }

        // Scope switcher dans le header (desktop) — persiste dans l'espace connecté,
        // masqué sur la config des thématiques et l'admin.
        if (scopeNav && !isQuizActive && !isThematiqueConfigPage && !isAdminPage) {
            const subPage = pathParts[3] === 'abonnement' ? 'Abonnement' : null;
            return (
                <div className="header-scope-nav">
                    {headerNavScroll.left && (
                        <button className="header-scope-nav-arrow" onClick={() => headerNavRef.current?.scrollBy({ left: -(headerNavRef.current?.clientWidth || 400), behavior: 'smooth' })} aria-hidden>
                            <ChevronLeft size={14} />
                        </button>
                    )}
                    <div className="header-scope-nav-inner" ref={headerNavRef} onScroll={checkHeaderNavScroll}>
                        {scopeNav.scopes.map(s => (
                            <Link
                                key={s.id}
                                to={scopeNav.isCompte ? `/compte/quiz/${s.slug}` : `/${s.slug}`}
                                className={`header-scope-pill ${s.image ? 'header-scope-pill--bg' : ''} ${s.slug === scopeNav.currentScope ? 'active' : ''}`}
                                style={{
                                    ...(s.couleur_theme ? { '--scope-color': s.couleur_theme } : {}),
                                    ...(s.image ? { backgroundImage: `url(${s.image})` } : {}),
                                }}
                            >
                                <span>{s.label}</span>
                            </Link>
                        ))}
                        {scopeNav.hasMore && (
                            scopeNav.noneSelected ? (
                                <Link to="/compte/account/thematiques" className="header-scope-pill header-scope-pill--browse">
                                    <LayoutGrid size={16} strokeWidth={2.25} />
                                    <span>Parcourir les thématiques</span>
                                </Link>
                            ) : (
                                <Link to="/compte/account/thematiques" className="header-scope-pill header-scope-pill--more" title="Voir toutes les thématiques">
                                    <Plus size={16} strokeWidth={2.5} />
                                </Link>
                            )
                        )}
                    </div>
                    {headerNavScroll.right && (
                        <button className="header-scope-nav-arrow" onClick={() => headerNavRef.current?.scrollBy({ left: headerNavRef.current?.clientWidth || 400, behavior: 'smooth' })} aria-hidden>
                            <ChevronRight size={14} />
                        </button>
                    )}
                    {subPage && <span className="header-scope-nav-subpage"><ChevronRight size={12} />{subPage}</span>}
                </div>
            );
        }

        // Breadcrumb pour les pages scope (/compte/quiz/:scope/...) — fallback si 1 seul scope
        // Skip for user scopes and hub (they have their own header)
        if (pathParts[0] === 'compte' && pathParts[1] === 'quiz' && pathParts[2]
            && !pathParts[2].startsWith('user-') && pathParts[2] !== 'mes-thematiques') {
            const scopeLabel = slugToLabel(pathParts[2]);
            const subPage = pathParts[3] === 'abonnement' ? 'Abonnement' : null;
            return (
                <div className="header-center">
                    <span className="header-center-label">{scopeLabel}</span>
                    {subPage && <><ChevronRight size={14} className="header-center-chevron" /><span className="header-center-sublabel">{subPage}</span></>}
                </div>
            );
        }

        // Accueil contextuel sur la config des thématiques (le scope-nav y est masqué) :
        // remplace le menu de thématiques par un petit mot d'accueil.
        if (isThematiqueConfigPage) {
            return (
                <div className="header-center header-center--greeting">
                    <span className="header-greeting__text">
                        Bonjour{user.prenom ? <> <strong>{user.prenom}</strong></> : ''}, on travaille sur quoi&nbsp;?
                    </span>
                </div>
            );
        }

        // Breadcrumb admin
        if (pathParts[0] === 'gestion') {
            const subLabel = GESTION_LABELS[pathParts[1]];
            return (
                <div className="header-center">
                    <span className="header-center-label">Administration</span>
                    {subLabel && <><ChevronRight size={14} className="header-center-chevron" /><span className="header-center-sublabel">{subLabel}</span></>}
                </div>
            );
        }

        return null;
    }

    const landingNavItems = isLandingPreview
        ? [
            { to: '/landingclaudehybride', label: 'Hybride Claude' },
            { to: '/', label: 'Home (Codex live)' },
            { to: '/landinghybridecompare', label: 'Comparatif' },
            { to: '/gestion/landings', label: 'Retour admin' },
        ]
        : [
            { hash: 'thematiques', label: 'Thématiques' },
            { to: '/fonctionnalites', label: 'Fonctionnalités' },
            { to: '/fonctionnalites#faq', label: 'Questions fréquentes' },
            { to: '/guide', label: 'Guide' },
            { to: '/ma-thematique', label: 'Ma Thématique' },
        ];

    // Fermer le menu au clic exterieur
    useEffect(() => {
        if (!menuOpen) return;
        const handleClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [menuOpen]);

    const handleNav = (path, tab) => {
        setMenuOpen(false);
        navigate(path, tab ? { state: { tab } } : undefined);
    };

    const handleLogout = () => {
        setMenuOpen(false);
        logout();
        navigate('/');
    };

    return (
        <>
        {impersonating && (
            <div className="impersonation-banner">
                <Eye size={16} />
                <span>Simulation : <strong>{impersonating.prenom || impersonating.email}</strong></span>
                <button onClick={async () => { await stopImpersonation(); window.location.assign('/gestion/users'); }} className="impersonation-banner-exit">
                    Quitter la simulation
                </button>
            </div>
        )}
        <header className="site-header">
            <a href={getLogoTarget(user)} className="header-logo-link" onClick={(e) => {
                e.preventDefault();
                const target = getLogoTarget(user);
                const current = window.location.pathname;
                if (current === target && window.scrollY > 50) {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                    navigate(target);
                }
            }}><BrandLogo width={247} /></a>
            {showQuickNav ? (
                <nav className="header-quick-nav" aria-label="Navigation rapide accueil">
                    {landingNavItems.map((item) => (
                        <Link key={item.to || item.hash} to={item.to || `/#${item.hash}`} className="nav-link header-quick-nav-link">
                            {item.label}
                        </Link>
                    ))}
                </nav>
            ) : (
                renderHeaderCenter()
            )}
            <div className="header-links">
                <ThemeToggle />
                {user ? (
                    <div className="header-user-menu" ref={menuRef}>
                        <button
                            className="header-user-btn header-user-btn--avatar"
                            onClick={() => setMenuOpen(!menuOpen)}
                            aria-label="Menu utilisateur"
                        >
                            {(user.prenom || user.email || '?').charAt(0).toUpperCase()}
                        </button>
                        {menuOpen && (
                            <div className="header-dropdown">
                                <div className="header-dropdown-hero">
                                    <div className="header-dropdown-hero-top">
                                        <div className="header-dropdown-hero-avatar">
                                            {(user.prenom || user.email || '?').charAt(0).toUpperCase()}
                                        </div>
                                        <div className="header-dropdown-hero-id">
                                            <span className="header-dropdown-hero-name">{user.prenom || 'Mon compte'}</span>
                                            <span className="header-dropdown-hero-email">{user.email}</span>
                                        </div>
                                    </div>
                                    <button onClick={() => handleNav(getLogoTarget(user))} className="header-dropdown-hero-cta">
                                        <BrandMark size={16} />
                                        <span>Accéder à mes Quiz</span>
                                        <ChevronRight size={16} className="header-dropdown-hero-cta-arrow" />
                                    </button>
                                </div>
                                <div className="header-dropdown-body">
                                <div className="header-dropdown-group-label">Mon compte</div>
                                <button onClick={() => handleNav('/compte/avancement')} className="header-dropdown-item">
                                    <Illustration className="header-dropdown-illu" svg={illuAvancement} color={menuColor} />
                                    <span>Mon avancement</span>
                                </button>
                                <button onClick={() => handleNav('/compte/account/thematiques')} className="header-dropdown-item">
                                    <Illustration className="header-dropdown-illu" svg={illuConfig} color={menuColor} />
                                    <span>Configuration des thématiques</span>
                                </button>
                                <button onClick={() => handleNav('/compte/account/infos')} className="header-dropdown-item">
                                    <Illustration className="header-dropdown-illu" svg={illuInfos} color={menuColor} />
                                    <span>Mes infos</span>
                                </button>
                                <button onClick={() => handleNav('/compte/account/achats')} className="header-dropdown-item">
                                    <Illustration className="header-dropdown-illu" svg={illuAchats} color={menuColor} />
                                    <span>Mes achats</span>
                                </button>
                                <button onClick={() => handleNav('/compte/account/parrainage')} className="header-dropdown-item">
                                    <Illustration className="header-dropdown-illu" svg={illuParrainer} color={menuColor} />
                                    <span>Parrainer · 1 semaine offerte</span>
                                </button>
                                <button onClick={() => handleNav('/compte/code')} className="header-dropdown-item">
                                    <Illustration className="header-dropdown-illu" svg={illuCode} color={menuColor} />
                                    <span>Activer un code</span>
                                </button>
                                {!!Number(user.is_formateur) && (
                                    <button onClick={() => handleNav('/formateur')} className="header-dropdown-item">
                                        <Illustration className="header-dropdown-illu" svg={illuFormateur} color={menuColor} />
                                        <span>Espace formateur</span>
                                    </button>
                                )}
                                {user.role === 'admin' && (
                                    <button onClick={() => handleNav('/gestion')} className="header-dropdown-item">
                                        <Illustration className="header-dropdown-illu" svg={illuAdmin} color={menuColor} />
                                        <span>Administration</span>
                                    </button>
                                )}
                                <div className="header-dropdown-group-label">Informations</div>
                                <div className="header-dropdown-chips">
                                    <button onClick={() => handleNav('/compte/guide')} className="header-dropdown-chip">
                                        <Illustration className="header-dropdown-chip-illu" svg={illuGuide} color={menuColor} />
                                        <span>Guide</span>
                                    </button>
                                    <button onClick={() => handleNav('/compte/account/faq')} className="header-dropdown-chip">
                                        <Illustration className="header-dropdown-chip-illu" svg={illuFaq} color={menuColor} />
                                        <span>FAQ</span>
                                    </button>
                                    <button onClick={() => handleNav('/compte/account/cgu')} className="header-dropdown-chip">
                                        <Illustration className="header-dropdown-chip-illu" svg={illuCgu} color={menuColor} />
                                        <span>CGU</span>
                                    </button>
                                </div>
                                {user.role === 'admin' && !impersonating && (
                                    <>
                                        <div className="header-dropdown-sep" />
                                        <div className="header-dropdown-group-label">Simuler un utilisateur</div>
                                        {gateBlocked ? (
                                            <button
                                                className="header-dropdown-item"
                                                onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('monquiz:admin_gate_required')); }}
                                                title="Accès gestion verrouillé — saisir le mot de passe d'accès"
                                            >
                                                <Lock size={16} />
                                                <span>Déverrouiller l&apos;accès gestion</span>
                                            </button>
                                        ) : (
                                        <div className="header-imp-search">
                                            <div className="header-imp-input-wrap">
                                                <Search size={14} />
                                                <input
                                                    type="text"
                                                    placeholder="Email ou prénom..."
                                                    value={impSearch}
                                                    onChange={(e) => { setImpSearch(e.target.value); searchUsers(e.target.value); }}
                                                    className="header-imp-input"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                {impSearch && (
                                                    <button className="header-imp-clear" onClick={(e) => { e.stopPropagation(); setImpSearch(''); setImpResults([]); }}>
                                                        <X size={12} />
                                                    </button>
                                                )}
                                            </div>
                                            {impResults.length > 0 && (
                                                <div className="header-imp-results">
                                                    {impResults.map(u => (
                                                        <button key={u.id} className="header-imp-result" onClick={() => handleImpersonate(u)}>
                                                            <span className="header-imp-result-name">{u.prenom || '—'}</span>
                                                            <span className="header-imp-result-email">{u.email}</span>
                                                            <span className="header-imp-result-action">Simuler</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            {impLoading && <div className="header-imp-loading">Recherche...</div>}
                                        </div>
                                        )}
                                    </>
                                )}
                                <div className="header-dropdown-foot">
                                    <button onClick={() => handleNav('/')} className="header-dropdown-foot-btn">
                                        <FaviconMark size={16} />
                                        <span>Accéder au site</span>
                                    </button>
                                    <button onClick={handleLogout} className="header-dropdown-foot-btn header-dropdown-foot-btn--logout">
                                        <LogOut size={16} strokeWidth={1.8} />
                                        <span>Déconnexion</span>
                                    </button>
                                </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="header-user-menu" ref={menuRef}>
                        <button
                            className="header-user-btn"
                            onClick={() => setMenuOpen(!menuOpen)}
                            aria-label="Connexion / Inscription"
                        >
                            <User size={20} strokeWidth={1.8} />
                        </button>
                        {menuOpen && (
                            <div className="header-dropdown header-dropdown--guest">
                                {/* En-tête gradient : accueil + CTA (inscription primaire, connexion en contour) */}
                                <div className="header-guest-head">
                                    <div className="header-guest-title">Bienvenue&nbsp;<span aria-hidden="true">👋</span></div>
                                    <p className="header-guest-sub">Entraîne-toi sur ton concours&nbsp;— prêt en 2&nbsp;minutes.</p>
                                    <button onClick={() => handleNav('/inscription')} className="header-guest-cta">
                                        <Illustration className="header-guest-illu" svg={illuCreer} color={menuColor} />
                                        <span>Créer mon compte gratuit</span>
                                    </button>
                                    <button onClick={() => handleNav('/connexion')} className="header-guest-login">
                                        <Illustration className="header-guest-illu" svg={illuConnexion} color={menuColor} />
                                        <span>Se connecter</span>
                                    </button>
                                    <p className="header-guest-trust">Sans carte bancaire · sans engagement</p>
                                </div>

                                {/* Bénéfices */}
                                <div className="header-guest-benefits">
                                    <div className="header-guest-benefit"><span className="header-guest-check"><Check size={13} strokeWidth={3} /></span>QCM taillés sur mesure pour ton concours</div>
                                    <div className="header-guest-benefit"><span className="header-guest-check"><Check size={13} strokeWidth={3} /></span>Suivi de progression détaillé</div>
                                    <div className="header-guest-benefit"><span className="header-guest-check"><Check size={13} strokeWidth={3} /></span>Apprentissage guidé par révision espacée</div>
                                </div>

                                {/* Découvrir (routes publiques) */}
                                <div className="header-guest-discover">
                                    <div className="header-dropdown-group-label">Découvrir</div>
                                    <button onClick={() => handleNav('/#thematiques')} className="header-dropdown-item">
                                        <Illustration className="header-dropdown-illu" svg={illuThematiques} color={menuColor} />
                                        <span>Thématiques</span>
                                    </button>
                                    <button onClick={() => handleNav('/fonctionnalites')} className="header-dropdown-item">
                                        <Illustration className="header-dropdown-illu" svg={illuFonctionnalites} color={menuColor} />
                                        <span>Fonctionnalités</span>
                                    </button>
                                    <button onClick={() => handleNav('/fonctionnalites#faq')} className="header-dropdown-item">
                                        <Illustration className="header-dropdown-illu" svg={illuQuestions} color={menuColor} />
                                        <span>Questions fréquentes</span>
                                    </button>
                                    <button onClick={() => handleNav('/guide')} className="header-dropdown-item">
                                        <Illustration className="header-dropdown-illu" svg={illuGuide} color={menuColor} />
                                        <span>Guide</span>
                                    </button>
                                    <button onClick={() => handleNav('/ma-thematique')} className="header-dropdown-item">
                                        <Illustration className="header-dropdown-illu" svg={illuMaThematique} color={menuColor} />
                                        <span>Ma Thématique</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </header>
        </>
    );
}
